import { useState, useCallback, useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { generateKeyBetween } from 'fractional-indexing';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { SBase, SText } from '@setvi/shared/components';
import {
  QuoteItemDTO,
  QuoteSectionDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import type { ReorderMove } from '../../hooks/use-reorder-items';
import { DraggableItem } from './draggable-item';
import { DraggableSectionItem } from './draggable-section';
import { DraggableProductItem } from './draggable-product-item';

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: '100%',
    overflowY: 'auto'
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1
  },
  dragOverlay: {
    opacity: 0.9,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
  }
}));

// Position helpers for fractional indexing
const getPos = (item: QuoteItemDTO): string | null =>
  (item as any).position || null;

const withPos = <T extends QuoteItemDTO>(item: T, position: string): T =>
  ({ ...item, position }) as any;

/**
 * Assign sequential fractional positions to all items in the array.
 * Ensures every item has a unique, correctly ordered position —
 * prevents issues when items share duplicate or null positions.
 */
export const assignPositions = <T extends QuoteItemDTO>(items: T[]): T[] => {
  let lastPos: string | null = null;
  return items.map(item => {
    const pos = generateKeyBetween(lastPos, null);
    lastPos = pos;
    return withPos(item, pos);
  });
};

/**
 * Recursively collect items whose position changed from the original.
 */
const collectMoves = (
  currentItems: QuoteItemDTO[],
  originalItems: QuoteItemDTO[],
  parentId: string | null
): ReorderMove[] => {
  const moves: ReorderMove[] = [];

  for (const item of currentItems) {
    const itemId = item.id || item._id;
    const originalItem = originalItems.find(i => (i.id || i._id) === itemId);

    const currentPos = getPos(item);
    const originalPos = originalItem ? getPos(originalItem) : null;

    if (currentPos && currentPos !== originalPos) {
      moves.push({
        itemId,
        position: currentPos,
        targetSectionId: parentId || null
      });
    }

    // Recurse into section children
    if (item.type === 'section' && item.children) {
      const origSection = originalItem as QuoteSectionDTO | undefined;
      moves.push(
        ...collectMoves(
          item.children as QuoteItemDTO[],
          (origSection?.children || []) as QuoteItemDTO[],
          itemId
        )
      );
    }

    // Recurse into product children
    if (item.type === 'product') {
      const product = item as QuoteProductDTO;
      const origProduct = originalItem as QuoteProductDTO | undefined;
      if (product.children?.length) {
        moves.push(
          ...collectMoves(
            product.children as QuoteItemDTO[],
            (origProduct?.children || []) as QuoteItemDTO[],
            itemId
          )
        );
      }
    }
  }

  return moves;
};

interface ReorderItemsDialogProps {
  items: QuoteItemDTO[];
  onClose: () => void;
  onSave: (moves: ReorderMove[]) => Promise<any>;
  isSaving?: boolean;
}

/**
 * Dialog for reordering products and sections in a quote
 * Supports drag and drop for both top-level items and products within sections
 */
export const ReorderItemsDialog = ({
  items,
  onClose,
  onSave,
  isSaving = false
}: ReorderItemsDialogProps) => {
  const classes = useStyles();

  // Local state for reordered items (before save)
  const [localItems, setLocalItems] = useState<QuoteItemDTO[]>(items);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );

  // Get IDs for sortable context
  const itemIds = useMemo(
    () => localItems.map(item => item.id || item._id),
    [localItems]
  );

  // Find active item for drag overlay
  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return localItems.find(item => (item.id || item._id) === activeId);
  }, [activeId, localItems]);

  // Render drag overlay content based on item type
  const renderDragOverlay = useCallback(() => {
    if (!activeItem) return null;

    if (activeItem.type === 'section') {
      return (
        <DraggableSectionItem
          section={activeItem as QuoteSectionDTO}
          isExpanded={false}
          onToggle={() => {}}
          onChildrenReorder={() => {}}
          isDragOverlay
        />
      );
    }

    const product = activeItem as QuoteProductDTO;
    if (product.children?.length > 0) {
      return (
        <DraggableProductItem
          product={product}
          onChildrenReorder={() => {}}
          isDragOverlay
        />
      );
    }

    return <DraggableItem item={product} isDragOverlay />;
  }, [activeItem]);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Toggle section expand/collapse
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  // Handle top-level drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over || active.id === over.id) return;

    setLocalItems(prevItems => {
      const oldIndex = prevItems.findIndex(
        item => (item.id || item._id) === active.id
      );
      const overIndex = prevItems.findIndex(
        item => (item.id || item._id) === over.id
      );

      if (oldIndex === -1 || overIndex === -1) return prevItems;

      const without = prevItems.filter((_, i) => i !== oldIndex);
      const insertIdx = overIndex;

      const result = [...without];
      result.splice(insertIdx, 0, prevItems[oldIndex]);
      return assignPositions(result);
    });
  }, []);

  // Handle reorder within a section
  const handleSectionChildrenReorder = useCallback(
    (sectionId: string, reorderedChildren: QuoteProductDTO[]) => {
      setLocalItems(prevItems =>
        prevItems.map(item => {
          const itemId = item.id || item._id;
          if (itemId === sectionId && item.type === 'section') {
            return {
              ...item,
              children: reorderedChildren
            } as QuoteSectionDTO;
          }
          return item;
        })
      );
    },
    []
  );

  // Handle reorder within a product (for product children like accessories)
  const handleProductChildrenReorder = useCallback(
    (productId: string, reorderedChildren: QuoteProductDTO[]) => {
      setLocalItems(prevItems =>
        prevItems.map(item => {
          const itemId = item.id || item._id;
          if (itemId === productId && item.type === 'product') {
            return {
              ...item,
              children: reorderedChildren
            } as QuoteProductDTO;
          }
          return item;
        })
      );
    },
    []
  );

  // Handle reorder of product children within a section
  const handleSectionProductChildrenReorder = useCallback(
    (
      sectionId: string,
      productId: string,
      reorderedChildren: QuoteProductDTO[]
    ) => {
      setLocalItems(prevItems =>
        prevItems.map(item => {
          const itemId = item.id || item._id;
          if (itemId === sectionId && item.type === 'section') {
            const section = item as QuoteSectionDTO;
            return {
              ...section,
              children: section.children.map(child => {
                const childId = child.id || child._id;
                if (childId === productId) {
                  return {
                    ...child,
                    children: reorderedChildren
                  };
                }
                return child;
              })
            } as QuoteSectionDTO;
          }
          return item;
        })
      );
    },
    []
  );

  // Compute moves on save - only items whose position changed
  const handleSave = useCallback(async () => {
    const moves = collectMoves(localItems, items, null);
    onClose();
    if (moves.length > 0) {
      await onSave(moves);
    }
  }, [localItems, items, onSave, onClose]);

  // Check if there are position changes
  const hasChanges = useMemo(
    () => collectMoves(localItems, items, null).length > 0,
    [localItems, items]
  );

  return (
    <SBase
      width={800}
      height={850}
      style={{ backgroundColor: '#FFFFFF' }}
      header={{
        title: 'Reorder',
        description:
          'Drag sections or items to set their order as it will appear in the quote preview.',
        onClose
      }}
      footer={{
        onConfirm: handleSave,
        onClose,
        confirmLabel: 'Save Changes',
        closeLabel: 'Cancel',
        disableConfirm: !hasChanges || isSaving,
        confirmLoading: isSaving
      }}>
      <Box className={classes.content}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}>
            <Box className={classes.itemsContainer}>
              {localItems.map(item => {
                const itemId = item.id || item._id;

                if (item.type === 'section') {
                  const section = item as QuoteSectionDTO;
                  const isExpanded = expandedSections.has(itemId);

                  return (
                    <DraggableSectionItem
                      key={itemId}
                      section={section}
                      isExpanded={isExpanded}
                      onToggle={toggleSection}
                      onChildrenReorder={handleSectionChildrenReorder}
                      onProductChildrenReorder={
                        handleSectionProductChildrenReorder
                      }
                    />
                  );
                }

                // Product at root level
                const product = item as QuoteProductDTO;
                const hasChildren =
                  product.children && product.children.length > 0;

                if (hasChildren) {
                  return (
                    <DraggableProductItem
                      key={itemId}
                      product={product}
                      onChildrenReorder={handleProductChildrenReorder}
                    />
                  );
                }

                return <DraggableItem key={itemId} item={product} />;
              })}
            </Box>
          </SortableContext>

          <DragOverlay>
            {activeItem ? (
              <Box className={classes.dragOverlay}>{renderDragOverlay()}</Box>
            ) : null}
          </DragOverlay>
        </DndContext>

        {localItems.length === 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}>
            <SText
              title="No items to reorder"
              style={{ color: '#5D7083', textAlign: 'center' }}
            />
          </Box>
        )}
      </Box>
    </SBase>
  );
};
